import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text,
} from 'react-native';
import {
  VictoryAxis, VictoryChart, VictoryLabel, VictoryVoronoiContainer, VictoryLine, VictoryScatter,
} from 'victory';
import Colors from '../constants/Colors';
import {
  axisDateLabel, ChartDataPoint, tickValSamples, tooltipDateLabel,
} from './axisUtils';
import { selectedLineColor } from './AirQualityGraph';
import { comparisonLineColor } from '../components/Home/HomeGraphLineSelector';
import { DateRange } from '../redux/dateRangeSlice';
import GetMetric, { AggregationFunction } from '../requests/Sensors/GetMetric';
import GetExternalEnvironmentData from '../requests/Sensors/GetExternalEnvironmentData';

const chartTheme = {
  axis: {
    stroke: 'none',
    style: {
      stroke: '#696D70',
      tickLabels: { fill: '#696D70' },
      grid: { stroke: 'none' },
    },
  },
};

interface MetricLineGraphProps {
  title: string,
  range: DateRange,
  compareRange: DateRange,
  metric: string,
  field?: string,
  hideTimeOnTooltip?: boolean,
}

const MetricLineGraph = ({
  title, range, compareRange, metric, field, hideTimeOnTooltip,
}: MetricLineGraphProps) => {
  const chartHeight: number = 540;
  const paddingVertical: number = 75;
  const paddingHorizontal: number = 60;
  const xAxisTickCount: number = 7;
  const [chartWidth, setChartWidth] = useState<number>(1000);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [comparisonData, setComparisonData] = useState<ChartDataPoint[]>([]);

  const showComparison: boolean = !!compareRange.start && !!compareRange.end;

  useEffect(() => {
    if (!range.start || !range.end) return;
    (field ? GetExternalEnvironmentData({
      metric,
      field,
      range,
    }) : GetMetric({
      metric,
      range,
      aggregationFunction: AggregationFunction.MAX,
    })).then((res) => {
      setChartData(res?.map((item, index: number) => ({
        index,
        date: item.timestamp,
        value: item.value ?? 0,
      })));
    });
  }, [range]);

  useEffect(() => {
    if (!showComparison) return;
    (field ? GetExternalEnvironmentData({
      metric,
      field,
      range: compareRange,
    }) : GetMetric({
      metric,
      range: compareRange,
      aggregationFunction: AggregationFunction.MAX,
    })).then((res) => {
      setComparisonData(res?.map((item, index: number) => ({
        index,
        date: item.timestamp,
        value: item.value ?? 0,
      })));
    });
  }, [compareRange]);

  const Line = (data: ChartDataPoint[], stroke: string) => <VictoryLine
    {...{ data }}
    style={{ data: { stroke, strokeWidth: 2 } }}
    x='index'
    y='value'
  />;

  const Scatter = (data: ChartDataPoint[], stroke: string) => <VictoryScatter
    {...{ data }}
    style={{ data: { fill: stroke } }}
    size={2}
    x='index'
    y='value'
  />;

  const XAxis = (tickValues, tickFormat, fill: string, dy: number) => <VictoryAxis
    crossAxis={false}
    tickCount={xAxisTickCount}
    {...{ tickValues, tickFormat }}
    fixLabelOverlap
    style={{
      axis: { stroke: 'none' },
      tickLabels: { fontSize: 14 },
    }}
    tickLabelComponent={<VictoryLabel
      {...{ dy }}
      dx={-20}
      style={{
        fill,
        fontSize: 14,
        strokeWidth: 0,
        fontFamily: 'Poppins_400Regular',
        textAnchor: 'center',
      }}
    />}
  />;

  const domainLimit: number = 1.3 * Math.max(
    ...(chartData ?? []).map((point) => point.value),
    ...(comparisonData ?? []).map((point) => point.value),
    1,
  );

  const tooltipCircle = (cx: number, value: number, stroke: string, limit: number) => !!value
    && <circle
      r={4}
      strokeWidth={3}
      {...{ stroke, cx }}
      cy={paddingVertical + (chartHeight - 1 - paddingVertical * 2) * (1 - value / limit)}
      fill='#FFF'
    />;

  class CustomFlyout extends React.Component {
    render() {
      const { x, y, datum } = this.props as any;
      const date: Date = chartData[datum.index]?.date
        ? new Date(chartData[datum.index]?.date) : null;
      const dateCompared: Date = comparisonData[datum.index]?.date
        ? new Date(comparisonData[datum.index]?.date) : null;
      const dateText: string = date
        ? tooltipDateLabel(chartData, datum.index, false, !hideTimeOnTooltip) : '-';
      const dateComparedText: string = dateCompared
        ? `vs. ${tooltipDateLabel(comparisonData, datum.index, false, hideTimeOnTooltip)}` : null;
      const valueText: string = `${title}: ${chartData[datum.index]
        ?.value ? `${Math.floor(chartData[datum.index]?.value)}` : '0'}`;
      const valueComparedText: string = `${title} (compared): ${comparisonData[datum.index]
        ?.value ? `${Math.floor(comparisonData[datum.index]?.value)}` : '-'}`;
      const containerHeight: number = showComparison ? 96 : 76;
      const containerWidth: number = (showComparison ? 100 : 50)
        + Math.max(
          dateText.length,
          (showComparison && comparisonData[datum.index]?.date) ? dateComparedText.length : 0,
          valueText.length,
          valueComparedText.length,
        ) * 6.5;
      const containerX: number = x > containerWidth + 20 ? x - containerWidth - 10 : x + 10;
      let containerY: number = Math.max(Math.min(y - containerHeight, 210 - containerHeight), 10);
      if (!Number.isFinite(containerY)) containerY = 10;

      const spot = (cy: number, fill: string) => <circle
        r={6}
        cx={containerX + 15}
        cy={containerY + cy}
        {...{ fill }}
      />;

      const label = (cy: number, text: string) => <text
        x={containerX + 30}
        y={containerY + cy}
        fontFamily={'Poppins_500Medium'}
        fontSize={12}
        strokeWidth={0}
        fill={Colors.light.hereworksBlack600}>
        {text}
      </text>;

      const dateLabel = (cy: number, text: string) => <text
        x={containerX + 10}
        y={containerY + cy}
        fontFamily={'Poppins_500Medium'}
        fontSize={12}
        strokeWidth={0}
        fill={Colors.light.hereworksBlack600}>
        {text}
      </text>;

      return <svg>
        <defs>
          <filter id={'f3_desk_bookings'} x={-0.5} y={-1} width='200%' height='250%'>
            <feOffset result='offOut' in='SourceGraphic' dx='0' dy='0' />
            <feColorMatrix result='matrixOut'
              in='offOut'
              type='matrix'
              values='0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0' />
            <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='4' />
            <feBlend in='SourceGraphic' in2='blurOut' mode='normal' result='colored' />
          </filter>
        </defs>
        {tooltipCircle(x, chartData[datum.index]?.value, selectedLineColor, domainLimit)}
        {tooltipCircle(x, comparisonData[datum.index]?.value, comparisonLineColor, domainLimit)}
        <rect
          x={containerX}
          y={containerY}
          width={containerWidth}
          height={containerHeight}
          stroke={selectedLineColor}
          rx={5}
          fill='white'
          filter={'url(#f3_desk_bookings)'}
        />
        {datum.date && dateLabel(20, dateText)}
        {showComparison && comparisonData[datum.index]?.date && dateLabel(40, dateComparedText)}
        <line
          x1={x}
          x2={x}
          y1={paddingVertical}
          y2={chartHeight - paddingVertical + 15}
          stroke={Colors.light.hereworksBlue100}
          strokeDasharray='10,10'
          strokeWidth={1}
        />
        {spot(showComparison ? 60.5 : 40.5, selectedLineColor)}
        {label(showComparison ? 65 : 45, valueText)}
        {showComparison && <>
          {spot(80.5, comparisonLineColor)}
          {label(85, valueComparedText)}
        </>}
      </svg>;
    }
  }

  return <View
    style={styles.chart}
    onLayout={(e) => setChartWidth(e.nativeEvent.layout.width)}
  >
    {title ? <Text style={styles.title}>{title}</Text> : null}
    <VictoryChart
      width={chartWidth}
      height={chartHeight}
      theme={chartTheme}
      domain={{ y: [0, domainLimit] }}
      containerComponent={<VictoryVoronoiContainer
        labels={({ datum }) => datum}
        voronoiDimension='x'
        labelComponent={<CustomFlyout />}
      />}
      padding={{
        top: paddingVertical,
        left: paddingHorizontal,
        right: paddingHorizontal,
        bottom: paddingVertical,
      }}
    >
      {XAxis(
        tickValSamples(
          [...Array(chartData?.length).keys()],
          Math.min(7, Math.floor(7 * ((chartData?.length ?? 1) / (comparisonData?.length ?? 1)))),
        ),
        (t) => ((chartData?.length > 0 && chartData[t]) ? axisDateLabel(chartData, t) : ''),
        Colors.light.hereworksBlack400,
        25,
      )}
      {showComparison && XAxis(
        tickValSamples(
          [...Array(comparisonData?.length).keys()],
          Math.min(7, Math.floor(7 * ((comparisonData?.length ?? 1) / (chartData?.length ?? 1)))),
        ),
        (t) => ((comparisonData?.length > 0 && comparisonData[t]) ? axisDateLabel(comparisonData, t) : ''),
        Colors.light.hereworksYellow600,
        45,
      )}
      <VictoryAxis
        dependentAxis
        domain={{ y: [0, domainLimit ?? 100] }}
        crossAxis={false}
        tickCount={5}
        tickFormat={(value: number) => Math.round((domainLimit > 2 ? 1 : 10) * value)
          / (domainLimit > 2 ? 1 : 10)}
        fixLabelOverlap
        style={{
          axis: { stroke: 'none' },
          tickLabels: { fontSize: 14 },
          grid: { stroke: Colors.light.hereworksWhite500, strokeWidth: 1, pointerEvents: 'none' },
        }}
        containerComponent={<VictoryVoronoiContainer />}
        tickLabelComponent={<VictoryLabel
          style={{
            fill: Colors.light.hereworksBlack400,
            fontSize: 14,
            strokeWidth: 0,
            fontFamily: 'Poppins_400Regular',
          }}
          dx={-12}
          textAnchor='end'
        />}
      />
      {Line(chartData, Colors.light.hereworksDarkBlue)}
      {Line(comparisonData, Colors.light.hereworksYellow500)}
      {Scatter(chartData, Colors.light.hereworksDarkBlue)}
      {Scatter(comparisonData, Colors.light.hereworksYellow500)}
    </VictoryChart>
    {!(chartData?.length > 0) && <View style={styles.descriptionContainer}>
      <Text style={styles.description}>{'No data available'}</Text>
    </View>}
  </View>;
};

const styles = StyleSheet.create({
  chart: {
    paddingStart: 24,
    paddingBottom: 20,
    width: '100%',
  },
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginTop: 24,
  },
  descriptionContainer: {
    position: 'absolute',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  description: {
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
});

export default MetricLineGraph;
